import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 5000;
const STORAGE_KEY = 'km_support_session';

const SupportChat = ({ enabled, welcomeMessage, accentColor = '#a855f7' }) => {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null); // {id, name, whatsapp}
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('open');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollTimerRef = useRef(null);
  const lastSeenRef = useRef(null);

  // Restore session from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) setSession(parsed);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Persist session
  useEffect(() => {
    if (session) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    }
  }, [session]);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  };

  const fetchMessages = useCallback(async (sid) => {
    try {
      const params = lastSeenRef.current ? { session_id: sid, since: lastSeenRef.current } : { session_id: sid };
      const res = await axios.get(`${API}/support/messages`, { params });
      const newMsgs = res.data?.messages || [];
      setStatus(res.data?.status || 'open');
      if (newMsgs.length > 0) {
        const latest = newMsgs[newMsgs.length - 1];
        lastSeenRef.current = latest.created_at;
        setMessages((prev) => {
          // Dedupe by id
          const ids = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...newMsgs.filter((m) => !ids.has(m.id))];
          return merged;
        });
        // Count admin messages while panel is closed
        if (!open) {
          const adminNew = newMsgs.filter((m) => m.sender === 'admin').length;
          if (adminNew > 0) setUnreadCount((c) => c + adminNew);
        }
        scrollToBottom();
      }
    } catch (e) { /* silent */ }
  }, [open]);

  // Polling loop when session active
  useEffect(() => {
    if (!session?.id) return;
    // Initial full fetch
    (async () => {
      try {
        const res = await axios.get(`${API}/support/messages`, { params: { session_id: session.id } });
        const msgs = res.data?.messages || [];
        setStatus(res.data?.status || 'open');
        setMessages(msgs);
        if (msgs.length > 0) lastSeenRef.current = msgs[msgs.length - 1].created_at;
        scrollToBottom();
      } catch (e) {
        // Session probably deleted server-side — reset
        setSession(null);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    })();
    pollTimerRef.current = setInterval(() => fetchMessages(session.id), POLL_INTERVAL_MS);
    return () => clearInterval(pollTimerRef.current);
  }, [session?.id, fetchMessages]);

  // Reset unread when panel opens
  useEffect(() => {
    if (open) setUnreadCount(0);
  }, [open]);

  const handleStart = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { setError('Adınızı yazın'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/support/start`, {
        name: name.trim(),
        whatsapp: whatsapp.trim() || undefined,
      });
      setSession({ id: res.data.id, name: res.data.name, whatsapp: res.data.whatsapp });
    } catch (e) {
      setError(e.response?.data?.detail || 'Çat açıla bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!draft.trim() || !session?.id || sending) return;
    setSending(true);
    const text = draft.trim();
    setDraft('');
    // Optimistic
    const optimistic = {
      id: 'opt-' + Date.now(),
      sender: 'user',
      text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    scrollToBottom();
    try {
      await axios.post(`${API}/support/message`, { session_id: session.id, text });
      // Remove the optimistic placeholder; server response will be picked up
      // by the next poll (no flicker because lastSeenRef advances naturally).
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      // Trigger immediate fetch to sync server-side ID
      fetchMessages(session.id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Mesaj göndərilmədi');
      // Remove optimistic on failure
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const handleEndChat = () => {
    setSession(null);
    setMessages([]);
    setStatus('open');
    lastSeenRef.current = null;
    sessionStorage.removeItem(STORAGE_KEY);
    setName('');
    setWhatsapp('');
    setDraft('');
  };

  if (!enabled) return null;

  return (
    <>
      {/* FAB button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="support-chat-fab"
          aria-label="Dəstək çatı aç"
          style={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 998,
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${accentColor} 0%, #6d28d9 100%)`,
            color: '#fff',
            border: 'none',
            boxShadow: '0 8px 24px rgba(168,85,247,0.45), 0 0 0 0 rgba(168,85,247,0.7)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'support-chat-pulse 2.4s ease-out infinite',
          }}
        >
          <MessageCircle size={26} />
          {unreadCount > 0 && (
            <span
              data-testid="support-chat-unread-badge"
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                background: '#ef4444',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                minWidth: 20,
                height: 20,
                padding: '0 6px',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #0a0520',
              }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="support-chat-panel"
          style={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            zIndex: 999,
            width: 'min(380px, calc(100vw - 32px))',
            maxHeight: 'min(560px, calc(100vh - 100px))',
            background: 'linear-gradient(180deg, #161028 0%, #0a0520 100%)',
            border: '1px solid rgba(168,85,247,0.3)',
            borderRadius: 16,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(168,85,247,0.1)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: 'Inter, sans-serif',
            color: '#e2e8f0',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 16px',
              background: 'linear-gradient(90deg, rgba(168,85,247,0.18) 0%, rgba(109,40,217,0.18) 100%)',
              borderBottom: '1px solid rgba(168,85,247,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${accentColor} 0%, #6d28d9 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MessageCircle size={18} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#f0e68c' }}>Dəstək çatı</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                  Adətən 30 dəq ərzində cavablayırıq
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="support-chat-close-btn"
              aria-label="Bağla"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                padding: 4,
                borderRadius: 6,
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          {!session ? (
            <form
              onSubmit={handleStart}
              style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}
            >
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                {welcomeMessage || 'Salam! Sualınızı bizə yazın.'}
              </div>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Adınız *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                required
                data-testid="support-chat-name-input"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                WhatsApp (istəyə bağlı)
              </label>
              <input
                type="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                maxLength={30}
                placeholder="+994 50 ..."
                data-testid="support-chat-whatsapp-input"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
              {error && (
                <div style={{ color: '#fca5a5', fontSize: 12 }} data-testid="support-chat-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="support-chat-start-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor} 0%, #6d28d9 100%)`,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginTop: 4,
                }}
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Çatı başlat
              </button>
            </form>
          ) : (
            <>
              {/* Messages list */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '14px 14px 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  WebkitOverflowScrolling: 'touch',
                  minHeight: 240,
                  maxHeight: 380,
                }}
                data-testid="support-chat-messages"
              >
                {messages.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`support-msg-${m.sender}`}
                    style={{
                      alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                      background:
                        m.sender === 'user'
                          ? `linear-gradient(135deg, ${accentColor}aa 0%, ${accentColor}66 100%)`
                          : 'rgba(255,255,255,0.06)',
                      border:
                        m.sender === 'user'
                          ? `1px solid ${accentColor}55`
                          : '1px solid rgba(255,255,255,0.08)',
                      color: '#fff',
                      padding: '8px 12px',
                      borderRadius: m.sender === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      fontSize: 13,
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.text}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              {status === 'closed' ? (
                <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
                    Bu sessiya bağlanıb.
                  </div>
                  <button
                    type="button"
                    onClick={handleEndChat}
                    data-testid="support-chat-new-btn"
                    style={{
                      width: '100%',
                      background: `linear-gradient(90deg, ${accentColor} 0%, #6d28d9 100%)`,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Yeni çat başlat
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSend}
                  style={{
                    padding: 10,
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    gap: 6,
                  }}
                >
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Mesajınız..."
                    maxLength={2000}
                    data-testid="support-chat-input"
                    style={{
                      flex: 1,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                    }}
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    data-testid="support-chat-send-btn"
                    aria-label="Göndər"
                    style={{
                      background: `linear-gradient(135deg, ${accentColor} 0%, #6d28d9 100%)`,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      width: 42,
                      cursor: sending || !draft.trim() ? 'not-allowed' : 'pointer',
                      opacity: sending || !draft.trim() ? 0.5 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes support-chat-pulse {
          0% { box-shadow: 0 8px 24px rgba(168,85,247,0.45), 0 0 0 0 rgba(168,85,247,0.7); }
          70% { box-shadow: 0 8px 24px rgba(168,85,247,0.45), 0 0 0 14px rgba(168,85,247,0); }
          100% { box-shadow: 0 8px 24px rgba(168,85,247,0.45), 0 0 0 0 rgba(168,85,247,0); }
        }
      `}</style>
    </>
  );
};

export default SupportChat;
